// Content pack (slice). Values are ported / inspired by the legacy br-perl
// data (plsinfo / xyinfo / weapons / NPC tiers). In production this becomes a
// JSON content-pack validated by pydantic (server) + zod (client).

import {
  PACK_ARCHETYPES,
  PACK_COMBAT,
  PACK_LOOT_WEAPONS,
  PACK_PLAYER_TEMPLATE,
  PACK_ZONES,
  WEATHER_COUNT as PACK_WEATHER_COUNT,
  WEATHER_FOG_INDEXES as PACK_WEATHER_FOG_INDEXES,
} from './JKGame_pack'
import type { Archetype, AttackRange, Weapon, WeaponKind } from './JKGame_types'

// Canonical Okishima (沖木島) locations, faithful to the Battle Royale novel's
// own island map: grid is A–J (rows, north→south) × 01–10 (cols, west→east).
// `desc` = deepened atmosphere shown on entering a room; `lore` = a novel
// event/character hook for story flavour. The School is a PERMANENT danger zone.
export type ZoneThemeId = 'lighthouse' | 'cliff' | 'shrine' | 'rock' | 'forest' | 'port' | 'beach' | 'water' | 'building'

// Pure data only — display name/desc/lore live in i18n under `zone.<id>.*`,
// so the whole system is localizable and the sim stays framework-free.
export interface ZoneDef {
  id: string
  coord: string // "C-05" → letter = row (A..J, N→S), number = col (01..10, W→E)
  region: 'north' | 'central' | 'south'
  danger?: boolean // permanently forbidden (collar detonates) — the Overseer's HQ
  theme: ZoneThemeId
}

// Loaded + zod-validated from content/packs/zones.json (single source of truth,
// mirrored on the backend via pydantic). Order == sim zone index; do not reorder.
export const ZONES: readonly ZoneDef[] = PACK_ZONES

// Permanently-forbidden zone indexes (the School / Sakamochi's HQ).
export const PERMANENT_DANGER_ZONES: ReadonlySet<number> = new Set(
  ZONES.map((z, i) => (z.danger ? i : -1)).filter((i) => i >= 0),
)

// Weather count from the pack. Display names live in i18n (`weather.w0`..),
// indexed by the sim's weather int — so this stays framework-free + localizable.
export const WEATHER_COUNT = PACK_WEATHER_COUNT

// Parse "C-05" -> { col, row } with the novel's orientation:
// letter = row (A=0 north … J=9 south), number = column (01=0 west … 10=9 east).
export function zoneGrid(coord: string): { col: number; row: number } {
  const [letter, num] = coord.split('-')
  return { col: Number(num) - 1, row: letter.charCodeAt(0) - 65 }
}

export const FISTS: Weapon = { name: '空手', kind: 'N', power: 0, ammo: -1, range: 'S' }

// Loot pool found via 探索. Loaded + zod-validated from
// content/packs/weapons.json (single source of truth, mirrored on the backend
// via pydantic).
export const LOOT_WEAPONS: readonly Weapon[] = PACK_LOOT_WEAPONS

// Stable item ids (display names live in i18n `item.<id>`); order preserved so
// spawnLoot's random index stays deterministic.
export const LOOT_ITEMS: readonly string[] = [
  'bandage',
  'energy_drink',
  'painkiller',
  'riceball',
  'vest_frag',
  'radar',
]

// A stat is either a scalar (no RNG draw on spawn) or a [min,max] range (one
// rng.int). The distinction is sim-significant — see archetypes.json note.
export type StatRange = number | [number, number]

// Per-archetype templates for spawning bots.
export interface ArchetypeTemplate {
  mhp: StatRange
  msp: StatRange
  att: StatRange
  def: StatRange
  skill: StatRange
  armor: number
  startWeapon: 'fists' | 'random' | Weapon
}

// Loaded + zod-validated from content/packs/player.json (single source of
// truth, mirrored on the backend via pydantic). All scalars — no RNG on spawn.
// (The starting blade keeps the player from being helpless on turn 1.)
export const PLAYER_TEMPLATE = PACK_PLAYER_TEMPLATE

// Global damage softener. Loaded from content/packs/combat.json (single source
// of truth, mirrored on the backend). Keeps a typical exchange at ~6–10 hits.
export const DAMAGE_SCALE = PACK_COMBAT.damageScale

// Loaded + zod-validated from content/packs/archetypes.json (single source of
// truth, mirrored on the backend via pydantic). Stat ranges are [min,max]
// tuples (rolled); scalars stay scalar (no RNG draw) — shape must match the sim.
export const ARCHETYPES: Record<Exclude<Archetype, 'player'>, ArchetypeTemplate> = PACK_ARCHETYPES

// Pseudo student-roster name pool (original, IP-safe placeholders).
export const NAME_POOL: readonly string[] = [
  '青木', '石田', '上田', '遠藤', '大野', '加藤', '木下', '工藤', '小林', '齋藤',
  '佐野', '清水', '杉本', '鈴木', '田中', '近藤', '中島', '西村', '橋本', '林',
  '原田', '平野', '福田', '藤井', '本田', '松本', '三浦', '宮本', '村上', '森田',
  '山口', '山田', '横山', '吉田', '渡邊', '安藤', '飯田', '岩本', '大塚', '岡田',
]

// 起霧/濃霧 → 視野受限。From the weather pack (single source of truth, both ends).
export const WEATHER_FOG_INDEXES: readonly number[] = PACK_WEATHER_FOG_INDEXES

export function rangeCompatible(a: AttackRange, b: AttackRange): boolean {
  // 反擊需射程相容（legacy）：同射程，或對方為中距(M)。
  return a === b || b === 'M'
}

// Combat constants — from content/packs/combat.json (zod-validated, mirrored on
// the backend). Values must match the sim exactly (same-seed determinism).
type BodyPartLite = 'b' | 'h' | 'a' | 'f'
export const HIT_BASE: Record<WeaponKind, number> = PACK_COMBAT.hitBase as Record<WeaponKind, number>
export const SKILL_DMG: Record<WeaponKind, number> = PACK_COMBAT.skillDmg as Record<WeaponKind, number>
export const INJURY_RATE: Record<WeaponKind, number> = PACK_COMBAT.injuryRate as Record<WeaponKind, number>
export const INJURY_PARTS: Record<WeaponKind, readonly BodyPartLite[]> =
  PACK_COMBAT.injuryParts as Record<WeaponKind, readonly BodyPartLite[]>
